export const EARLIER_APU_PROGRAMMES = [
  "Undergraduate Programme",
  "Postgraduate Programme",
  "PG Diploma/Certificate Programme",
];

export const APU_EMAIL_DOMAIN = "@apu.edu.in";
export const APF_EMAIL_DOMAIN = "@azimpremjifoundation.org";

export const hasEmailDomain = (email, domain) =>
  String(email || "")
    .toLowerCase()
    .trim()
    .endsWith(domain);

const normalize = (value = "") =>
  String(value)
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const stripProgramName = (value = "") =>
  normalize(value)
    .replace(/\./g, "")
    .replace(/honou?rs/g, "honours")
    .replace(/[()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const isCampus = (name, key) => {
  const n = normalize(name);
  if (key === "bhopal") return n.includes("bhopal");
  if (key === "bangalore")
    return n.includes("bangalore") || n.includes("banglore");
  if (key === "ranchi") return n.includes("ranchi");
  return false;
};

export const UG_BHOPAL_PREF2_PROGRAMS = [
  "BA Honours (Economics)",
  "BSc Honours (Biology)",
  "BA Honours (English)",
  "BA Honours (History)",
  "BA Honours (Social Science)",
];

const matchUgBhopalPref2 = (name) => {
  const n = stripProgramName(name);
  if (!n || n.includes("itep")) return "";
  if (n.includes("social science")) return "social science";
  if (n.includes("english")) return "english";
  if (n.includes("history")) return "history";
  if (n.includes("biology")) return "biology";
  if (
    n.includes("economics") &&
    !n.includes("philosophy") &&
    !n.includes("applied")
  ) {
    return "economics";
  }
  return "";
};

const isUgBhopalPref2Program = (name) => Boolean(matchUgBhopalPref2(name));

const toArray = (value) => (Array.isArray(value) ? value : []);

export const getCampusesForProgram = (campusPreference, programName) => {
  const map = campusPreference || {};
  if (Array.isArray(map[programName])) return map[programName];
  const target = stripProgramName(programName);
  if (!target) return [];
  const matchedKey = Object.keys(map).find(
    (key) => stripProgramName(key) === target
  );
  return toArray(matchedKey ? map[matchedKey] : []);
};

const campusesFor = (campusPreference, programName) =>
  getCampusesForProgram(campusPreference, programName);

const hasCampusKey = (campuses, key) =>
  toArray(campuses).some((campus) => isCampus(campus.value, key));

export const getCampusNameById = (campuses, id) =>
  toArray(campuses).find((campus) => campus.Id === id)?.value || "";

export const getPreferenceCourseName = (course) => {
  if (!course) return "";
  if (typeof course === "string") return course;
  return course.value || course.Name || course.label || course.name || "";
};

export const getUgPref2Courses = ({
  coursePreference,
  campusPreference = {},
  pref1ProgramId,
  pref1CampusName,
}) => {
  const courses = toArray(coursePreference);
  const noSecond = courses.filter(
    (course) => getPreferenceCourseName(course) === "No Second Preference"
  );
  const others = courses.filter(
    (course) => getPreferenceCourseName(course) !== "No Second Preference"
  );

  if (isCampus(pref1CampusName, "bhopal")) {
    return [
      ...others.filter(
        (course) =>
          isUgBhopalPref2Program(getPreferenceCourseName(course)) &&
          course.Id !== pref1ProgramId
      ),
      ...noSecond,
    ];
  }

  if (isCampus(pref1CampusName, "bangalore")) {
    return [
      ...others.filter((course) => {
        const campuses = campusesFor(
          campusPreference,
          getPreferenceCourseName(course)
        );
        const allBhopalPrograms =
          isUgBhopalPref2Program(getPreferenceCourseName(course)) ||
          hasCampusKey(campuses, "bhopal");
        const remainingBangalorePrograms =
          hasCampusKey(campuses, "bangalore") && course.Id !== pref1ProgramId;
        return allBhopalPrograms || remainingBangalorePrograms;
      }),
      ...noSecond,
    ];
  }

  return courses;
};

export const getUgPref2Campuses = ({
  campuses,
  pref1CampusName,
  pref1ProgramName,
  pref2ProgramName,
}) => {
  const campusList = toArray(campuses);
  let filtered = campusList;

  if (isCampus(pref1CampusName, "bhopal")) {
    filtered = campusList.filter((campus) => isCampus(campus.value, "bhopal"));
  } else if (isCampus(pref1CampusName, "bangalore")) {
    filtered = campusList.filter((campus) => !isCampus(campus.value, "ranchi"));
  }

  if (
    stripProgramName(pref1ProgramName) &&
    stripProgramName(pref1ProgramName) === stripProgramName(pref2ProgramName)
  ) {
    const selectedCampus = normalize(pref1CampusName);
    filtered = filtered.filter(
      (campus) => normalize(campus.value) !== selectedCampus
    );
  }

  return filtered;
};

export const matchPgProgram = (name) => {
  const n = stripProgramName(name);
  if (!n) return "";
  if (n.includes("applied economics")) return "appliedEconomics";
  if (n.includes("climate")) return "climate";
  if (n.includes("public health") || n === "mph") return "mph";
  if (n.includes("ecce") || n.includes("early child")) return "ecce";
  if (n.includes("business administration") || n.includes("mba")) return "mba";
  if (n.includes("education")) return "education";
  if (n.includes("development")) return "development";
  if (n.includes("economics")) return "economics";
  return "";
};

const PG_PREF2_MATRIX = {
  development: [
    { key: "education", campuses: ["bangalore", "bhopal"] },
    { key: "ecce", campuses: ["bangalore"] },
    { key: "mph", campuses: ["bhopal"] },
  ],
  education: [
    { key: "ecce", campuses: ["bangalore"] },
    { key: "development", campuses: ["bangalore", "ranchi"] },
  ],
  ecce: [{ key: "education", campuses: ["bangalore", "bhopal"] }],
  climate: [{ key: "development", campuses: ["bangalore", "ranchi"] }],
  mph: [
    { key: "development", campuses: ["bangalore", "ranchi"] },
    { key: "education", campuses: ["bangalore", "bhopal"] },
  ],
  economics: [
    { key: "appliedEconomics", campuses: ["ranchi"] },
    { key: "development", campuses: ["bangalore", "ranchi"] },
  ],
  appliedEconomics: [
    { key: "economics", campuses: ["bangalore"] },
    { key: "development", campuses: ["ranchi"] },
  ],
};

export const getPgPref2Courses = ({
  coursePreference,
  firstProgramName,
}) => {
  const courses = toArray(coursePreference);
  const allowed = PG_PREF2_MATRIX[matchPgProgram(firstProgramName)];
  const noSecond = courses.filter(
    (course) => course.value === "No Second Preference"
  );
  if (!firstProgramName || !allowed) {
    return noSecond;
  }
  const others = courses.filter((course) => {
    if (course.value === "No Second Preference") return false;
    return allowed.some(
      (item) => item.key === matchPgProgram(course.value)
    );
  });
  return [...others, ...noSecond];
};

export const getPgPref2Campuses = ({
  campuses,
  firstProgramName,
  secondProgramName,
}) => {
  const campusList = toArray(campuses);
  const allowed = PG_PREF2_MATRIX[matchPgProgram(firstProgramName)];
  const rule = allowed?.find(
    (item) => item.key === matchPgProgram(secondProgramName)
  );
  if (!rule) return campusList;
  return campusList.filter((campus) =>
    rule.campuses.some((key) => isCampus(campus.value, key))
  );
};
